import React, { useEffect } from "react";

import { FormProvider, useWatch } from "react-hook-form";

import { ClientId } from "@fartherfinance/frontend/api/Types";

import { useHouseholdContext } from "../../context";
import {
  HouseholdEstablishedAtFormField,
  HouseholdNameFormField,
} from "../../HouseholdFormFields";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import FormBody from "@src/sharedComponents/Forms/FormBody";
import Text from "@src/sharedComponents/Typography/Text";
import { pluralize } from "@src/utils/pluralize";

import AddHouseholdMember from "./AddHouseholdMember";
import { HouseholdMemberRelationshipFormField } from "./HouseholdMemberRelationshipFormField";
import ProfileSkeleton from "./ProfileSkeleton";

import styles from "./HouseholdProfile.module.css";

export default function HouseholdProfile() {
  const { form, householdId, isLoading } = useHouseholdContext();
  const { control } = form;

  useEffect(() => {
    trackEvent({
      name: "Advisor Open Household Profile Tab",
    });
  }, []);

  const householdMembers = useWatch({
    control,
    name: "members",
  });

  const householdContacts = useWatch({
    control,
    name: "pointsOfContact",
  });

  const householdMembersCount =
    Object.keys(householdMembers).length +
    Object.keys(householdContacts).length;

  if (isLoading) {
    return (
      <div className={styles.profileContainer}>
        <ProfileSkeleton />
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <form className={styles.profileContainer}>
        <FormBody className={styles.sectionSpacing}>
          <HouseholdNameFormField label="Household" />
          <HouseholdEstablishedAtFormField label="Established On (Optional)" />
        </FormBody>
        <Text variant="title3" className={styles.titleSpacing}>
          {householdMembersCount} Household{" "}
          {pluralize(householdMembersCount, "Contact", false)}
        </Text>
        {Object.keys(householdContacts).map((clientId) => (
          <HouseholdMemberRelationshipFormField
            memberName={`pointsOfContact.${clientId}.memberOption`}
            relationshipName={`pointsOfContact.${clientId}.relationshipOption`}
            pointOfContactName={`pointsOfContact.${clientId}.pointOfContact`}
            key={clientId}
            className={styles.memberFormField}
            withDeleteButton
            clientId={clientId as ClientId}
            householdId={householdId}
          />
        ))}
        {Object.keys(householdMembers).map((clientId) => (
          <HouseholdMemberRelationshipFormField
            memberName={`members.${clientId}.memberOption`}
            relationshipName={`members.${clientId}.relationshipOption`}
            pointOfContactName={`members.${clientId}.pointOfContact`}
            key={clientId}
            className={styles.memberFormField}
            withDeleteButton
            clientId={clientId as ClientId}
            householdId={householdId}
          />
        ))}
        <AddHouseholdMember />
      </form>
    </FormProvider>
  );
}
